<template>
  <v-card>
  <v-card-title class="d-flex align-center justify-space-between">
      <div>
        <v-icon left>mdi-chart-line</v-icon>
        Comeback Offers Timeline
      </div>
    </v-card-title>
    <v-card-text>
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <div v-else>
        <apexchart
          type="line"
          height="380"
          :options="chartOptions"
          :series="series"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import { statisticsService } from "../services";

export default {
  name: "ComebackOffersTimelineApex",
  components: { apexchart: VueApexCharts },
  props: {
    startDate: { type: [String, Date], required: true },
    endDate: { type: [String, Date], required: true },
  },
  data() {
    return {
      loading: true,
      raw: null,
      series: [],
      chartOptions: {
        chart: {
          id: "comeback-offers-timeline",
          type: "line",
          toolbar: { show: false },
          animations: { enabled: true },
        },
        stroke: { curve: "smooth", width: 3 },
        dataLabels: { enabled: false },
        xaxis: { type: "datetime" },
        yaxis: {
          decimalsInFloat: 0,
          labels: { formatter: (val) => Math.round(val) },
        },
        legend: { position: "top" },
        tooltip: { 
          x: { format: "yyyy MMM dd" },
          y: {
            formatter: function (val, { seriesIndex, dataPointIndex, w }) {
              const seriesName = w.globals.seriesNames[seriesIndex];
              return `${val} ${seriesName.toLowerCase()}`;
            }
          }
        },
        colors: ["#1976D2", "#4CAF50", "#FF9800"],
      },
    };
  },
  computed: {
    // Single key for both bounds so moving the range triggers one reload.
    rangeKey() {
      return `${this.toYmd(this.startDate)}|${this.toYmd(this.endDate)}`;
    },
  },
  watch: {
    rangeKey() { this.loadTimelineData(); },
  },
  methods: {
    async loadTimelineData() {
      this.loading = true;
      try {
        const startYmd = this.toYmd(this.startDate);
        const endYmd = this.toYmd(this.endDate);
        const response = await statisticsService.getComebackOfferStatistics({
          period: 'custom',
          startIso: startYmd,
          endIso: endYmd
        });
        if (response?.success) {
          this.raw = response.data;
          this.applyData();
        } else {
          this.series = [];
        }
      } catch (e) {
        console.error("Failed to load comeback offers timeline", e);
        this.series = [];
      } finally {
        this.loading = false;
      }
    },
    toYmd(d) {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    applyData() {
      if (!this.raw || !this.raw.timeline) {
        this.series = [];
        return;
      }
      
      const dates = this.raw.timeline.map((d) => new Date(d.date).getTime());
      
      const series1 = {
        name: "CB's Sent",
        data: this.raw.timeline.map((day) => [
          new Date(day.date).getTime(),
          day.comebackOffersTotal || 0,
        ]),
      };
      const series2 = {
        name: "CB's Viewed",
        data: this.raw.timeline.map((day) => [
          new Date(day.date).getTime(),
          day.comebackOffersWithClicks || 0,
        ]),
      };
      const series3 = {
        name: "Quotes from CB's",
        data: this.raw.timeline.map((day) => [
          new Date(day.date).getTime(),
          day.quotesWithMatchingComebackOffers || 0,
        ]),
      };
      
      this.series = [series1, series2, series3];
      
      this.chartOptions = {
        ...this.chartOptions,
        xaxis: { type: "datetime", categories: dates },
      };
    },
  },
  mounted() {
    this.loadTimelineData();
  },
};
</script>

<style scoped>
.py-8 {
  padding-top: 32px;
  padding-bottom: 32px;
}
</style>
